import { useEffect, useMemo, useRef, useState } from 'react'
import { reportEdit, reportEditDebounced } from './activity'
import { rebalance } from './rebalance'
import {
  buildLines, lineId,
  type AvailableConfig, type Block, type ConfigRow, type EditLine,
  type NewBlockSpec, type PredictResponse,
} from './domain'

export function useEditablePlan(result: PredictResponse | null, modelCodes: string[]) {
  const [lines, setLines] = useState<EditLine[]>([])
  const [blocks, setBlocks] = useState<Block[]>([])
  const [planN, setPlanN] = useState(50)

  // When reloading a cart item we re-hydrate its saved edits rather than the raw
  // prediction: primeRestore() stages them, then the caller swaps in result and
  // this effect applies the staged snapshot instead of rebuilding from scratch.
  const pendingRestore = useRef<{ lines: EditLine[]; blocks: Block[]; planN: number } | null>(null)
  function primeRestore(snapshot: { lines: EditLine[]; blocks: Block[]; planN: number }) {
    pendingRestore.current = snapshot
  }

  useEffect(() => {
    if (!result) return
    const restore = pendingRestore.current
    pendingRestore.current = null
    if (restore) { setLines(restore.lines); setBlocks(restore.blocks); setPlanN(restore.planN) }
    else { setLines(buildLines(result)); setBlocks(result.blocks); setPlanN(result.n_cars) }
  }, [result])

  // Two-level pin-and-flow: planN splits across blocks (by block share, honoring
  // block locks), then each block's cars split across its rows (by row weight,
  // honoring row locks). Locking/editing a block reflows the others; locking a
  // row reflows the other rows in the same block — the block total is unaffected.
  const allocation = useMemo(() => {
    if (!result) return null
    const byBlock = new Map<number, EditLine[]>()
    for (const l of lines) {
      const arr = byBlock.get(l.blockRank) ?? []
      arr.push(l)
      byBlock.set(l.blockRank, arr)
    }
    // Level 1 — distribute planN across blocks. Empty blocks get 0 (no rows to hold cars).
    const blockUnits = blocks.map(b => {
      const hasRows = (byBlock.get(b.rank)?.length ?? 0) > 0
      return {
        key: String(b.rank),
        weight: hasRows ? Math.max(b.weight_share, 0) : 0,
        pin: hasRows ? (b.pin ?? null) : null,
      }
    })
    const blockReb = rebalance(blockUnits, planN)
    // Level 2 — distribute each block's cars across its rows.
    const quantities: Record<string, number> = {}
    let status = blockReb.status
    let message = blockReb.message
    for (const b of blocks) {
      const members = byBlock.get(b.rank) ?? []
      const blockQty = blockReb.quantities[String(b.rank)] ?? 0
      const rowReb = rebalance(members.map(l => ({ key: l.id, weight: l.weight, pin: l.pin })), blockQty)
      Object.assign(quantities, rowReb.quantities)
      if (status === 'ok' && rowReb.status !== 'ok') {
        status = rowReb.status
        message = `Block ${b.rank}: ${rowReb.message}`
      }
    }
    const total = Object.values(quantities).reduce((a, n) => a + n, 0)
    return { quantities, total, status, message }
  }, [result, lines, blocks, planN])

  const cfgLabel = (l: { color: string | null; interior: string | null; rim: string | null }) =>
    [l.color, l.interior, l.rim].filter(Boolean).join(' / ') || '—'
  const pkgLabel = (pkgs: string[]) => pkgs.join('+') || '(no package)'

  function onPlanN(n: number) {
    const v = Math.max(0, n)
    setPlanN(v)
    reportEditDebounced('plann', modelCodes, `Changed order size to ${v} cars`)
  }
  function setPin(id: string, qty: number) {
    const l = lines.find(x => x.id === id)
    setLines(ls => ls.map(l => l.id === id ? { ...l, pin: Math.max(0, Math.floor(qty || 0)) } : l))
    if (l) reportEditDebounced(`pin:${id}`, modelCodes, `Locked ${cfgLabel(l)} to ${Math.max(0, Math.floor(qty || 0))} cars`)
  }
  function unpin(id: string) {
    const l = lines.find(x => x.id === id)
    setLines(ls => ls.map(l => l.id === id ? { ...l, pin: null } : l))
    if (l) reportEdit(modelCodes, `Unlocked ${cfgLabel(l)}`)
  }
  function setBlockPin(rank: number, qty: number) {
    const b = blocks.find(x => x.rank === rank)
    setBlocks(bs => bs.map(b => b.rank === rank ? { ...b, pin: Math.max(0, Math.floor(qty || 0)) } : b))
    if (b) reportEditDebounced(`blockpin:${rank}`, modelCodes, `Set block ${pkgLabel(b.packages)} to ${Math.max(0, Math.floor(qty || 0))} cars`)
  }
  function unpinBlock(rank: number) {
    const b = blocks.find(x => x.rank === rank)
    setBlocks(bs => bs.map(b => b.rank === rank ? { ...b, pin: null } : b))
    if (b) reportEdit(modelCodes, `Unlocked block ${pkgLabel(b.packages)}`)
  }
  function removeLine(id: string) {
    const l = lines.find(x => x.id === id)
    setLines(ls => ls.filter(l => l.id !== id))
    if (l) reportEdit(modelCodes, `Removed ${cfgLabel(l)}`)
  }
  function addLine(blockRank: number, packages: string[], cand: AvailableConfig, blockCars: number) {
    const r = { color: cand.color, interior: cand.interior, rim: cand.rim, trim: cand.trim } as ConfigRow
    const id = lineId(blockRank, r)
    // weight on the same car-count scale as model rows, from its historical share of the block
    const weight = Math.max(1, Math.round(cand.weight_share * blockCars))
    setLines(ls => ls.some(l => l.id === id) ? ls : [...ls, {
      id, blockRank, packages, color: cand.color, interior: cand.interior, rim: cand.rim, trim: cand.trim,
      weight, weight_share: cand.weight_share, pin: null, source: 'user',
    }])
    reportEdit(modelCodes, `Added ${cfgLabel(cand)}`)
  }
  function deleteBlock(rank: number) {
    const b = blocks.find(x => x.rank === rank)
    setBlocks(bs => bs.filter(b => b.rank !== rank))
    setLines(ls => ls.filter(l => l.blockRank !== rank))
    if (b) reportEdit(modelCodes, `Deleted block ${pkgLabel(b.packages)}`)
  }
  // A manually added block is seeded with the edition's typical rows and demand share,
  // so it behaves like a predicted one: it takes a share of the plan and its rows
  // auto-balance. With no seed rows (a brand-new combo with no history) it starts
  // pinned to 0 so the user sets the count and adds rows manually.
  function addBlock(spec: NewBlockSpec) {
    const rank = blocks.reduce((m, b) => Math.max(m, b.rank), 0) + 1
    const seeded: EditLine[] = spec.rows.map(r => ({
      id: lineId(rank, r as ConfigRow), blockRank: rank, packages: spec.packages,
      color: r.color, interior: r.interior, rim: r.rim, trim: r.trim,
      weight: Math.max(r.weight_share, 0), weight_share: r.weight_share, pin: null, source: 'model',
    }))
    const newBlock: Block = {
      rank, packages: spec.packages, single_options: [], base_options: [],
      rim_from_package: false, trim_from_package: false, rows: [], n_cars: 0,
      weight_share: spec.weight_share,
      overall_confidence: 0, confidence_label: 'LOW', step_confidences: [], violations: [],
      nmc_model_code: spec.nmc_model_code, nmc_edition_code: spec.nmc_edition_code,
      nmc_edition_name: spec.nmc_edition_name, nmc_extra_packages: spec.nmc_extra_packages,
      nmc_included: [], source: 'user', pin: seeded.length ? null : 0,
    }
    setBlocks(bs => [...bs, newBlock])
    setLines(ls => [...ls, ...seeded])
    reportEdit(modelCodes, `Added block ${spec.nmc_model_code ?? pkgLabel(spec.packages)}`)
  }
  function setBlockAddons(rank: number, options: string[], extras: string[]) {
    const b = blocks.find(x => x.rank === rank)
    setBlocks(bs => bs.map(b => b.rank === rank
      ? { ...b, single_options: options, nmc_extra_packages: extras } : b))
    if (b) reportEdit(modelCodes, `Set add-ons on block ${b.nmc_model_code ?? pkgLabel(b.packages)}: ${[...extras, ...options].join(', ') || '(none)'}`)
  }
  function resetEdits() {
    if (result) { setLines(buildLines(result)); setBlocks(result.blocks); setPlanN(result.n_cars) }
    reportEdit(modelCodes, 'Reset all edits')
  }

  const pinnedCount = lines.filter(l => l.pin !== null).length + blocks.filter(b => b.pin != null).length
  const originalCount = result ? result.blocks.reduce((s, b) => s + b.rows.length, 0) : 0
  const removedCount = Math.max(0, originalCount - lines.filter(l => l.source === 'model').length)
  const addedCount = lines.filter(l => l.source === 'user').length
  const editCount = pinnedCount + removedCount + addedCount +
    (result && planN !== result.n_cars ? 1 : 0)

  return {
    lines, blocks, planN, onPlanN, allocation, pinnedCount, editCount,
    setPin, unpin, setBlockPin, unpinBlock, removeLine, addLine,
    deleteBlock, addBlock, setBlockAddons, resetEdits, primeRestore,
  }
}

export type EditablePlan = ReturnType<typeof useEditablePlan>

// The reusable result view: allocation status + editable blocks + add-block panel.
// Both the single view and each batch model section render this against their own plan.
